// Marketing site behaviour. Standalone bundle: no Turbo, no Stimulus, no app
// code. Three jobs: the nav hairline, scroll-triggered reveals, and the hero
// demo played in the design system's Ink rhythm.
//
// The full demo transcript is server-rendered, so without JavaScript (or with
// reduced motion) the page is complete and static. Everything here is
// progressive enhancement.

import { wait, writeInInk } from "./lib/ink.js"

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

// --- nav: a rule appears under the bar once the page starts moving ---------

const nav = document.querySelector("[data-nav]")
if (nav) {
  const update = () => nav.classList.toggle("nav--scrolled", window.scrollY > 8)
  document.addEventListener("scroll", update, { passive: true })
  update()
}

// --- scroll reveals --------------------------------------------------------

// Section titles draw their umber pen-stroke as they arrive; staggered
// containers settle their children in sequence.

const observe = (element, onEnter, threshold = 0.5) => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      observer.disconnect()
      onEnter()
    })
  }, { threshold })
  observer.observe(element)
}

if (!reducedMotion && "IntersectionObserver" in window) {
  document.querySelectorAll("[data-reveal]").forEach((title) => {
    observe(title, () => title.classList.add("is-on"))
  })

  document.querySelectorAll("[data-stagger]").forEach((container) => {
    container.classList.add("is-armed")
    observe(container, () => container.classList.add("is-on"), 0.35)
  })
}

// --- the demo: Breezy writing, in Ink --------------------------------------

// Timing and the pen tip live in lib/ink.js, shared with the preview page so
// the two surfaces write at the same pace.

const playDemo = async (demo) => {
  for (const step of demo.querySelectorAll("[data-demo-step]")) {
    const kind = step.dataset.demoStep
    step.classList.add("is-played")

    if (kind === "ink") {
      await writeInInk(step.querySelector("p"))
      await wait(500)
    } else if (kind === "chips") {
      await wait(700)
    } else {
      await wait(900)
    }
  }
}

// The demo loops while it's actually being watched: it pauses between plays
// whenever the card is off-screen or the tab is hidden, and rewinds before
// each replay. writeInInk re-reads textContent, so no transcript snapshot is
// needed between runs.
const loopDemo = async (demo, isVisible) => {
  const steps = [...demo.querySelectorAll("[data-demo-step]")]

  for (;;) {
    await playDemo(demo)
    await wait(6000)
    while (document.hidden || !isVisible()) await wait(1000)
    steps.forEach((step) => step.classList.remove("is-played"))
    await wait(700)
  }
}

const demo = document.querySelector("[data-demo]")
if (demo && !reducedMotion && "IntersectionObserver" in window) {
  // The full transcript stays in the DOM; each step is re-revealed (and
  // re-written, for ink steps) in order.
  demo.classList.add("demo--armed")

  let visible = false
  new IntersectionObserver((entries) => {
    entries.forEach((entry) => { visible = entry.isIntersecting })
  }, { threshold: 0.45 }).observe(demo)

  observe(demo, () => loopDemo(demo, () => visible), 0.45)
}

// --- pricing value calculator ----------------------------------------------

// Mirrors Marketing::ValueCalculator. The page is server-rendered with the
// default example, so this only runs on input; the constants come from data
// attributes so Ruby stays the single source of truth.

const calc = document.querySelector("[data-calculator]")
if (calc) {
  const wage = parseFloat(calc.dataset.wage) || 0
  const deflection = parseFloat(calc.dataset.deflection) || 0
  const gbp = (n) => n.toLocaleString("en-GB")
  const out = (key) => calc.querySelector(`[data-calc-output="${key}"]`)
  const setText = (key, value) => { const el = out(key); if (el) el.textContent = value }
  const readInput = (key) => {
    const el = calc.querySelector(`[data-calc-input="${key}"]`)
    return Math.max(0, parseInt(el && el.value, 10) || 0)
  }

  // The headline savings figure counts up to its new value; static under
  // reduced motion.
  let tween = null
  const setSavings = (value) => {
    const el = out("cost_savings")
    if (!el) return
    if (reducedMotion) { el.textContent = gbp(value); return }
    const from = parseInt(el.textContent.replace(/[^\d]/g, ""), 10) || 0
    if (tween) cancelAnimationFrame(tween)
    const start = performance.now()
    const step = (now) => {
      const t = Math.min(1, (now - start) / 420)
      const eased = 1 - Math.pow(1 - t, 3)
      el.textContent = gbp(Math.round(from + (value - from) * eased))
      if (t < 1) tween = requestAnimationFrame(step)
    }
    tween = requestAnimationFrame(step)
  }

  const recompute = () => {
    const bookings = readInput("bookings")
    const contacts = readInput("contacts")
    const minutes = readInput("minutes")

    const contactRate = bookings > 0 ? Math.round((contacts / bookings) * 10) / 10 : 0
    const supportHours = Math.round((contacts * minutes) / 60)
    const estimatedCost = Math.round(supportHours * wage)
    const hoursSaved = Math.round(supportHours * deflection)
    const costSavings = Math.round(estimatedCost * deflection)
    const residualCost = estimatedCost - costSavings
    const spentPct = estimatedCost > 0 ? Math.round((residualCost * 100) / estimatedCost) : 0

    setText("contact_rate", contactRate)
    setText("support_hours", gbp(supportHours))
    setText("estimated_cost", gbp(estimatedCost))
    setText("hours_saved", gbp(hoursSaved))
    setText("residual_cost", gbp(residualCost))
    setText("cost_savings_viz", gbp(costSavings))
    setSavings(costSavings)

    const spent = calc.querySelector("[data-calc-spent]")
    const saved = calc.querySelector("[data-calc-saved]")
    if (spent) spent.style.width = `${spentPct}%`
    if (saved) saved.style.width = `${100 - spentPct}%`
  }

  // Runs only when the operator hits "calculate" (or presses enter in a field),
  // not on every keystroke. The page ships with the default example computed.
  const form = calc.querySelector("[data-calc-form]")
  if (form) {
    form.addEventListener("submit", (event) => {
      event.preventDefault()
      recompute()
    })
  }
}

// --- Turnstile forms: one submit per token --------------------------------

// A Turnstile token is single-use, so a double tap posted a second request
// with a spent token and the visitor was told to verify (or, on the demo
// form, that their request had not gone). A page restored from the
// back-forward cache returns with the button still disabled and the token
// still spent, so both are reset then. reset() is given the form's own
// widget: with no argument it resets whichever widget rendered last.
document.querySelectorAll("form").forEach((form) => {
  const widget = form.querySelector(".cf-turnstile")
  if (!widget) return

  const submit = form.querySelector("[type=submit]")
  form.addEventListener("submit", () => {
    if (submit) submit.disabled = true
  })
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return
    if (submit) submit.disabled = false
    window.turnstile?.reset?.(widget)
  })
})
